import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import BACKEND_URL from '../../vnog/config';

interface DocumentationFeedbackItem {
    id: number;
    documentation_retrieved_id: number;
    rating: number;
    comment?: string;
    created_at: string;
    created_by_email?: string;
    document_title?: string;
}

interface EventData {
    id: number;
    order?: number;
    start_time?: string;
    end_time?: string;
    event_type: string;
    message_content?: string;
    message_sender?: string;
    other_arguments?: any;
}

interface DocumentationFeedbackDetail {
    id: number;
    rating: number;
    comment?: string;
    created_at: string;
    created_by_email?: string;
    document_title?: string;
    document_summary?: string;
    document_id?: string;
    similarity_score?: number;
    search_type?: string;
    simulation_data?: {
        id: number;
        token: string;
        created_at: string;
        status: string;
        address?: {
            id: number;
            street: string;
            zipcode: string;
            house_number: string;
        };
        event_log: EventData[];
    };
    documentation_search_event?: EventData;
}

interface DocumentationFeedbackProps {
    onLoadingChange?: (loading: boolean) => void;
}

const stars = [1, 2, 3, 4, 5];

const FeedbackDetailPanel: React.FC<{ detail: DocumentationFeedbackDetail | null }> = ({ detail }) => {
    if (!detail) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="text-gray-500 text-center">Select a feedback item to view details</div>
            </div>
        );
    }

    const targetEventId = detail.documentation_search_event?.id;

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Documentation Feedback Details</h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Rating</div>
                        <div className="flex items-center gap-1">
                            {stars.map((s) => (
                                <span key={s} className={s <= detail.rating ? 'text-amber-400' : 'text-gray-300'}>
                                    &#9733;
                                </span>
                            ))}
                            <span className="ml-1 text-sm text-gray-600">{detail.rating}/5</span>
                        </div>
                    </div>

                    <div>
                        <div className="text-sm text-gray-500 mb-1">Comment</div>
                        <div className="text-sm text-gray-700 break-words">{detail.comment || 'No comment provided'}</div>
                    </div>

                    <div>
                        <div className="text-sm text-gray-500 mb-1">Created By</div>
                        <div className="text-sm text-gray-700">{detail.created_by_email || 'Unknown'}</div>
                    </div>

                    <div>
                        <div className="text-sm text-gray-500 mb-1">Created At</div>
                        <div className="text-sm text-gray-700">{new Date(detail.created_at).toLocaleString()}</div>
                    </div>

                    <div>
                        <div className="text-sm text-gray-500 mb-2">Document Info</div>
                        <div className="bg-gray-50 p-3 rounded space-y-1">
                            <div className="text-sm"><strong>Title:</strong> {detail.document_title || '-'}</div>
                            {detail.document_id && (
                                <div className="text-sm"><strong>Document ID:</strong> {detail.document_id}</div>
                            )}
                            {detail.document_summary && (
                                <div className="text-sm"><strong>Summary:</strong> {detail.document_summary}</div>
                            )}
                            {detail.similarity_score != null && (
                                <div className="text-sm"><strong>Similarity Score:</strong> {detail.similarity_score.toFixed(4)}</div>
                            )}
                            {detail.search_type && (
                                <div className="text-sm"><strong>Search Type:</strong> {detail.search_type}</div>
                            )}
                        </div>
                    </div>

                    {detail.documentation_search_event && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Documentation Search Event</div>
                            <div className="bg-gray-50 p-3 rounded">
                                <div className="text-sm"><strong>Event ID:</strong> {detail.documentation_search_event.id}</div>
                                <div className="text-sm"><strong>Type:</strong> {detail.documentation_search_event.event_type}</div>
                            </div>
                        </div>
                    )}

                    {detail.simulation_data && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Simulation Details</div>
                            <div className="bg-gray-50 p-3 rounded">
                                <div className="text-sm"><strong>Simulation ID:</strong> {detail.simulation_data.id}</div>
                                <div className="text-sm"><strong>Status:</strong> {detail.simulation_data.status}</div>
                                <div className="text-sm"><strong>Created:</strong> {new Date(detail.simulation_data.created_at).toLocaleString()}</div>
                                {detail.simulation_data.address && (
                                    <div className="text-sm">
                                        <strong>Address:</strong> {detail.simulation_data.address.street} {detail.simulation_data.address.house_number}, {detail.simulation_data.address.zipcode}
                                    </div>
                                )}
                                <div className="text-sm"><strong>Events:</strong> {detail.simulation_data.event_log.length} events</div>
                            </div>
                        </div>
                    )}
                </div>

                {detail.simulation_data && detail.simulation_data.event_log && detail.simulation_data.event_log.length > 0 && (
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Event Log</div>
                        <div className="bg-gray-50 p-3 rounded max-h-96 overflow-y-auto">
                            <div className="space-y-2">
                                {detail.simulation_data.event_log.map((event, index) => {
                                    const isTargetEvent = targetEventId != null && event.id === targetEventId;
                                    return (
                                        <div
                                            key={event.id}
                                            className={`p-3 rounded border ${
                                                isTargetEvent
                                                    ? 'bg-blue-100 border-blue-300 ring-2 ring-blue-400'
                                                    : 'bg-white border-gray-200'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center space-x-2">
                                                    <span className={`text-xs px-2 py-1 rounded ${
                                                        isTargetEvent
                                                            ? 'bg-blue-200 text-blue-800'
                                                            : 'bg-gray-200 text-gray-700'
                                                    }`}>
                                                        #{index + 1}
                                                    </span>
                                                    <span className={`text-xs px-2 py-1 rounded ${
                                                        event.event_type === 'ai_operator'
                                                            ? 'bg-green-100 text-green-800'
                                                            : event.event_type === 'message'
                                                            ? 'bg-purple-100 text-purple-800'
                                                            : event.event_type === 'documentation_search'
                                                            ? 'bg-teal-100 text-teal-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {event.event_type}
                                                    </span>
                                                    {isTargetEvent && (
                                                        <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800 font-medium">
                                                            Search Event
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    ID: {event.id}
                                                </div>
                                            </div>

                                            {event.message_content && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Message:</div>
                                                    <div className="text-sm text-gray-700 break-words">{event.message_content}</div>
                                                </div>
                                            )}

                                            {event.message_sender && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Sender:</div>
                                                    <div className="text-sm text-gray-700">{event.message_sender}</div>
                                                </div>
                                            )}

                                            {event.other_arguments && Object.keys(event.other_arguments).length > 0 && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Additional Data:</div>
                                                    <div className="text-xs text-gray-600 bg-gray-100 p-2 rounded">
                                                        <pre className="whitespace-pre-wrap break-words">
                                                            {JSON.stringify(event.other_arguments, null, 2)}
                                                        </pre>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                {event.start_time && (
                                                    <div>Start: {new Date(event.start_time).toLocaleString()}</div>
                                                )}
                                                {event.end_time && (
                                                    <div>End: {new Date(event.end_time).toLocaleString()}</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const DocumentationFeedback: React.FC<DocumentationFeedbackProps> = ({ onLoadingChange }) => {
    const [feedbackList, setFeedbackList] = useState<DocumentationFeedbackItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [detailsCache, setDetailsCache] = useState<Map<number, DocumentationFeedbackDetail>>(new Map());
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchList = async () => {
        setLoading(true);
        onLoadingChange?.(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/feedback/documentation_feedback/`, {
                credentials: 'include',
            });
            if (response.ok) {
                setFeedbackList(await response.json());
            } else {
                toast.error('Failed to fetch documentation feedback');
                setFeedbackList([]);
            }
        } catch {
            toast.error('Error fetching documentation feedback');
            setFeedbackList([]);
        } finally {
            setLoading(false);
            onLoadingChange?.(false);
        }
    };

    const fetchDetail = async (id: number) => {
        if (detailsCache.has(id)) {
            setSelectedId(id);
            return;
        }
        setLoadingDetail(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/feedback/documentation_feedback/${id}/`, {
                credentials: 'include',
            });
            if (response.ok) {
                const detail = await response.json();
                setDetailsCache(prev => new Map(prev).set(id, detail));
                setSelectedId(id);
            } else {
                toast.error('Failed to fetch feedback details');
            }
        } catch {
            toast.error('Error fetching feedback details');
        } finally {
            setLoadingDetail(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    if (loading) {
        return <div className="px-6 py-4 bg-white rounded-lg shadow">Loading…</div>;
    }

    if (feedbackList.length === 0) {
        return <div className="px-6 py-4 bg-white rounded-lg shadow text-gray-500">No data</div>;
    }

    const selectedDetail = selectedId ? detailsCache.get(selectedId) : null;

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-semibold mb-1">Documentation Feedback</h2>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-1">
                    <div className="space-y-3">
                        {feedbackList.map((item) => (
                            <div
                                key={item.id}
                                className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-colors ${
                                    selectedId === item.id
                                        ? 'ring-2 ring-blue-500 bg-blue-50'
                                        : 'hover:bg-gray-50'
                                }`}
                                onClick={() => fetchDetail(item.id)}
                            >
                                <div className="text-sm text-gray-500 mb-1">Document</div>
                                <div className="text-sm font-medium text-gray-900 mb-2 break-words">
                                    {item.document_title || '-'}
                                </div>
                                <div className="text-sm text-gray-500 mb-1">Rating</div>
                                <div className="text-sm font-medium text-gray-900 mb-2">{item.rating}/5</div>
                                <div className="text-sm text-gray-500 mb-1">Comment</div>
                                <div className="text-sm text-gray-700 mb-2 break-words line-clamp-2">
                                    {item.comment || 'No comment'}
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                    <div>By: {item.created_by_email || '-'}</div>
                                    <div>{new Date(item.created_at).toLocaleDateString()}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-3">
                    {loadingDetail ? (
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="text-center">Loading details...</div>
                        </div>
                    ) : (
                        <FeedbackDetailPanel detail={selectedDetail || null} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DocumentationFeedback;
